import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';
import type { CorpsJeton, JetonPresente, ReponseEmission, ReponseJetons } from './accesApi.js';

export type { CorpsJeton, JetonPresente } from './accesApi.js';
export interface OutilAssistant {
  readonly nom: string;
  readonly titre: string;
  readonly scopes: readonly string[];
  readonly lecture: boolean;
}
export interface ReponseAccesAssistant extends ReponseJetons {
  readonly urlMcp: string;
  readonly outils: readonly OutilAssistant[];
}
const chemin = (g: string, suite = '') => `/g/${encodeURIComponent(g)}/assistant-mcp${suite}`;
export const assistantMcpApi = {
  lister: (g: string, appel?: AppelApi) =>
    api.get<ReponseAccesAssistant>(chemin(g, '/acces'), { galerie: g, ...appel }),
  emettre: (g: string, corps: CorpsJeton, appel?: AppelApi) =>
    api.post<ReponseEmission>(chemin(g, '/acces'), corps, { galerie: g, ...appel }),
  revoquer: (g: string, id: string, appel?: AppelApi) =>
    api.post<{ jeton: JetonPresente }>(
      chemin(g, `/acces/${encodeURIComponent(id)}/revoquer`),
      {},
      { galerie: g, ...appel },
    ),
};
export function useAccesAssistant(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'assistant-mcp', 'acces'),
    queryFn: (c) => assistantMcpApi.lister(g, appelDeRequete(c)),
  });
}
/** Pas de MutationCache : même gcTime:0 retiendrait le secret pendant l'observation. */
export function useEmettreAccesAssistant(g: string) {
  const client = useQueryClient();
  const [isPending, setPending] = useState(false);
  const enCours = useRef(false);
  const mutateAsync = async (corps: CorpsJeton): Promise<ReponseEmission> => {
    if (enCours.current) throw new Error('Émission déjà en cours.');
    enCours.current = true;
    setPending(true);
    try {
      const resultat = await dansLaPorteeCourante((appel) =>
        assistantMcpApi.emettre(g, corps, appel),
      );
      void client.invalidateQueries({ queryKey: cleMetier(g, 'assistant-mcp') });
      return resultat;
    } finally {
      enCours.current = false;
      setPending(false);
    }
  };
  return { isPending, mutateAsync };
}
export function useRevoquerAccesAssistant(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => assistantMcpApi.revoquer(g, id, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'assistant-mcp') });
      },
    }),
  );
}
